import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import { ONE_MINUTE_IN_MILLIS } from "@mirage/shared/util/constants";
import { register, unregister } from "@mirage/shared/util/jobs";
import { shouldSyncRecommendations } from "./utils/should-sync-recommendations";

import {
  getOrgChart,
  getPeopleSuggestions,
  getPeopleTypeaheadSuggestions,
} from "@mirage/service-people";
import type { PersonObject } from "@mirage/service-people/types";
import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import type { PeopleSyncResult } from "@mirage/service-typeahead-search/service/types";
import {
  deduplicateAndMergeExtractedPeople,
  extractValidPersonFromRecommendation,
  removeDeactivatedAccounts,
  toPeopleRecommendation,
} from "@mirage/shared/search/people-recommendations";
import { Recommendation } from "@mirage/shared/search/recommendation";
import { isDefined } from "@mirage/shared/util/tiny-utils";

const logger = tagged("typeahead/sync-people-recommendations");

const SYNC_RECOMMENDATIONS_JOB_NAME = "people-recommendations-sync";
export const SYNC_INTERVAL_MS = ONE_MINUTE_IN_MILLIS * 20;

export function start(cache: TypeaheadCache) {
  register(SYNC_RECOMMENDATIONS_JOB_NAME, SYNC_INTERVAL_MS, true, () =>
    sync(cache),
  );
}

export function cancel() {
  unregister(SYNC_RECOMMENDATIONS_JOB_NAME);
}

// sync, clear, and insert logic
const metrics = namespace("typeahead");
export async function sync(
  cache: TypeaheadCache,
  forceSync: boolean = false,
): Promise<PeopleSyncResult> {
  let shouldClearSensitiveData = true;
  try {
    const lastPeopleRecommendationsSyncMs =
      await cache.getLastPeopleRecommendationsSyncMs();

    // Remove this check once Dash People is rolled out on desktop
    const isDesktop = EnvCtx.surface == "desktop";
    if (
      (!isDesktop &&
        shouldSyncRecommendations(
          lastPeopleRecommendationsSyncMs,
          "people-recommendations",
          SYNC_INTERVAL_MS,
        )) ||
      forceSync
    ) {
      const [peopleTypeaheadSuggestions, orgChart] = await Promise.all([
        getPeopleTypeaheadSuggestions(),
        getOrgChart(),
      ]);
      let personObjects: PersonObject[] = [];
      if (orgChart.length === 0) {
        // PeopleTypeaheadSuggestions are useless without the org chart, as they don't have any metadata (e.g. display name, profile photo URL)
        // The org chart will be empty if the People feature is disabled
        // In this case, we should fall back to PeopleSuggestions
        personObjects = await getPeopleSuggestions({
          limit: 100,
          returnTeamMembersIfEmpty: true,
        });
        metrics.stats("sync/count", personObjects.length, {
          object_type: "get_people_suggestions",
        });
      } else {
        const suggestionsByAccountId = new Map(
          peopleTypeaheadSuggestions.map((p) => [p.accountId, p]),
        );
        let scoredPeople = 0;
        personObjects = orgChart
          .filter((person) => {
            // Filter out people without an email address
            // This is needed for employees with donotuse@dropbox.com email
            // since we set their email to an empty string to exclude them from
            // search results
            return person.email;
          })
          .map((person) => {
            if (suggestionsByAccountId.has(person.accountId)) {
              // Merge in the score, predictionId, and reason from the suggestions, if available
              //
              // Each attribute is explicitly listed here, rather than using
              // `...user` to avoid including any attributes found in
              // PersonOrgChart that are not present in PeopleRecommendation.
              // This is specifically done for the `birthday` attribute, which
              // is sensitive data and has special handling to avoid caching
              // for longer than desired.
              const suggestion = suggestionsByAccountId.get(person.accountId)!;
              return {
                uuid: person.uuid,
                accountId: person.accountId,
                managerUuid: person.managerUuid,
                email: person.email,
                displayName: person.displayName,
                displayHandle: person.displayHandle,
                profilePhotoUrl: person.profilePhotoUrl,
                directReportCount: person.directReportCount,
                totalReportCount: person.totalReportCount,
                onLeave: person.onLeave,
                isDeactivated: person.isDeactivated,
                role: person.role,
                score: suggestion.score,
                predictionId: suggestion.predictionId,
                reason: suggestion.reason,
              };
              scoredPeople++;
            } else {
              // Otherwise, use the unscored person from the org chart
              return {
                uuid: person.uuid,
                accountId: person.accountId,
                managerUuid: person.managerUuid,
                email: person.email,
                displayName: person.displayName,
                displayHandle: person.displayHandle,
                profilePhotoUrl: person.profilePhotoUrl,
                directReportCount: person.directReportCount,
                totalReportCount: person.totalReportCount,
                onLeave: person.onLeave,
                isDeactivated: person.isDeactivated,
                role: person.role,
              };
            }
          });
        // All people from the org chart; some have scores and some do not
        metrics.stats("sync/count", personObjects.length, {
          object_type: "people_org_chart",
        });
        // Count how many people from the org chart were merged with scores from get_typeahead_suggestions
        metrics.stats("sync/count", scoredPeople, {
          object_type: "people_org_chart_matched_with_scores",
        });
        // Count how many results from get_typeahead_suggestions were not used, because no matching person was found in the org chart
        metrics.stats("sync/count", personObjects.length - scoredPeople, {
          object_type: "people_scores_not_matched_with_org_chart",
        });
      }

      metrics.counter("sync/status", 1, {
        status: "success",
      });

      const omniboxRecommendations = cache.all(
        CacheKey.OmniboxRecommendations,
      ) as Recommendation<"omnibox">[];

      const extractedPersonObjects = omniboxRecommendations
        .map(extractValidPersonFromRecommendation)
        .filter(isDefined);

      const mergedPeople = deduplicateAndMergeExtractedPeople(
        extractedPersonObjects,
        personObjects,
      );

      // toPeopleRecommendation simply adds a uuid if one is not present
      // getOrgChart returns people with a uuid, but getPeopleTypeaheadSuggestions and getPeopleSuggestions do not
      const peopleRecommendations = mergedPeople.map(toPeopleRecommendation);

      const activePeopleRecommendations = peopleRecommendations.filter(
        removeDeactivatedAccounts,
      );

      await cache.clear(CacheKey.PeopleRecommendations);
      await cache.clear(CacheKey.OrgChart);
      shouldClearSensitiveData = false;
      await cache.cachePeopleRecommendations(activePeopleRecommendations);
      await cache.cacheOrgChart(orgChart);
      const timestamp = await cache.savePeopleRecommendationsSyncedMs();
      return {
        peopleRecommendationsLength: activePeopleRecommendations.length,
        orgChartLength: orgChart.length,
        timestamp,
      };
    }
    return {
      peopleRecommendationsLength: 0,
      orgChartLength: 0,
      timestamp: null,
    };
  } catch (e) {
    logger.error("sync failed", e);
    metrics.counter("sync/status", 1, {
      status: "error",
    });
    if (shouldClearSensitiveData) {
      await cache.clearBirthdaysFromOrgChart();
      shouldClearSensitiveData = false;
    }
    throw e;
  }
  if (shouldClearSensitiveData) {
    await cache.clearBirthdaysFromOrgChart();
  }
}
